import { useMemo } from 'react'

import {
  type ReplaceParamsOptions,
  URLReplaceOption,
  useBaseUrlParams,
} from './base-url-param.hook'

export enum UrlParam {
  SCOPE_ID = 'scopeId',
  RULE_ID = 'ruleId',
  THIRD_PARTY_FILTER = 'thirdParty',
  // Kept for legacy deep links (e.g. older Jira tickets); the inventory page redirects ?thirdPartyId=X to /third-parties/X.
  THIRD_PARTY_ID = 'thirdPartyId',
  ENGAGEMENT_ID = 'engagementId',
  INVENTORY_VIEW = 'inventoryView',
  FOURTH_PARTY_COMPANY_ID = 'fourthPartyCompanyId',
  ALERT_ID = 'alertId',
  SIGNAL_ID = 'signalId',
  BACKOFFICE_SIGNAL_ID = 'backofficeSignalId',
  CREATE_RISK = 'createRisk',
  RISKS_ONLY = 'risksOnly',
  CONTROL_ID = 'controlId',
  CONTROL_SETTING_ID = 'controlSettingId',
  READ_ONLY_CONTROL_ID = 'readOnlyControlId',
  READ_ONLY_SIGNAL_ID = 'readOnlySignalId',
  READ_ONLY_FINDING_ID = 'readOnlyFindingId',
  SETTING_SECTION = 'settingSection',
  VIEW = 'view',
  CONTROL_VIEW = 'controlView',
  STEP = 'step',
  THIRD_PARTY_ACTIVITY_LOG_ID = 'thirdPartyActivityLogId',
  COMPANY_DRAWER_TAB = 'companyDrawerTab',
  QUESTIONNAIRE_ID = 'questionnaireId',
  HIGHLIGHTED_QUESTION_ID = 'highlightedQuestionId',
  RECIPIENT_QUESTIONNAIRE = 'recipientQuestionnaire',
  PROJECT_ID = 'projectId',
  CONTROL_TIMELINE_TAB = 'controlTimelineTab',
  COMMENT_ID = 'commentId',
  VIEW_AS_ROLE = 'viewAsRole',
  ASSESSMENT_TAB = 'assessmentTab',
  SELECTED_ASSESSMENT_ID = 'selectedAssessmentId',
  PAST_ASSESSMENT_TAB = 'pastAssessmentTab',
  DASHBOARD_TAB = 'dashboardTab',
  DASHBOARD_DRAFT_ID = 'draftId',
  DASHBOARD_PERIOD = 'period',
  REQUEST_ID = 'requestId',
  DISCOVERY_SOLUTION_ID = 'discoverySolutionId',
  SCOPE_TAB = 'scopeTab',
  NOTIFICATION_TAB = 'notificationTab',
}

interface UrlParamsHookResult {
  urlParams: Record<UrlParam, string | null>
  updateParam: (param: UrlParam, value: string, options?: URLReplaceOption) => void
  removeParam: (param: UrlParam) => void
  replaceParams: (params: ReplaceParamsOptions<UrlParam>) => void
}

export const useUrlParams = (shouldCheckUnsavedChanges = false): UrlParamsHookResult => {
  const {
    urlParams: baseParams,
    updateParam,
    removeParam,
    replaceParams,
  } = useBaseUrlParams(shouldCheckUnsavedChanges)

  const urlParams = useMemo(
    () =>
      Object.values(UrlParam).reduce(
        (acc, param) => {
          acc[param] = baseParams[param] ?? null
          return acc
        },
        {} as Record<UrlParam, string | null>,
      ),
    [baseParams],
  )

  return {
    urlParams,
    updateParam,
    removeParam,
    replaceParams,
  }
}
